/**
 * Resets the Design System with the provided object.
 * @param {object} obj - Object to reset the Design System with.
 * @param {object} [opts={}] - Options for resetting the Design System.
 */
export function setDesignSystem(obj, opts = {}) {
  const { version, ...value } = obj
  const designSystem = (this.getData().designSystem = value)

  this.sdk.updateData(['update', ['designSystem'], designSystem], {
    message: 'Reset Design System',
    ...opts
  })

  this.updateDesignSystemOnCanvas(this.getDesignSystem(), opts)
}

/**
 * Updates the Design System on the canvas.
 * @param {object} obj - Object containing the Design System data.
 */
export function updateDesignSystemOnCanvas(obj) {
  const Collection = this.getCanvasCollection()
  if (!Collection) {
    return
  }
  Collection.call('reinit', Collection.call('deepClone', obj), {
    preventInject: true
  })
  const vars = Collection.lookup('vars')
  if (vars?.update) {
    vars.update()
  }
}

/**
 * Retrieves the Design System data.
 *
 * Categories live at lowercase keys in stored data (`designSystem.icons`,
 * `designSystem.color`, `designSystem.typography`, …) — same casing the
 * Icon component, scratch CONFIG, and example fixtures use. We probe
 * both UPPER and lower historically to tolerate legacy data that the
 * old broken `updateDesignSystem` path uppercased before saving, but
 * new writes land at the lowercase canonical key.
 *
 * @param {string} [type] - Optional category to retrieve.
 * @returns {object} - The Design System data.
 */
export function getDesignSystem(type) {
  const designSystem = this.getData('designSystem')
  // `getBuiltins()` reads root state DEFAULTS.DS_DEFAULTS, which a host that
  // never seeded the defaults simply does not have. The stored design system
  // below is already treated as optional (`designSystem?.[upper]`) — the
  // builtins get the same treatment, or the spread throws and takes both
  // ThemeDropdown and ThemeSelect down with it.
  const defaultData = this.getBuiltins() || {}
  if (type) {
    const lower = String(type).toLowerCase()
    const upper = String(type).toUpperCase()
    return {
      ...defaultData[upper],
      ...defaultData[lower],
      ...designSystem?.[upper],
      ...designSystem?.[lower]
    }
  }
  return designSystem
}

/**
 * Checks if a field is part of the built-in Design System.
 * @param {string} type - Category type (e.g., 'colors', 'typography').
 * @param {string} field - Field name to check.
 * @returns {boolean} - True if the field is part of the built-in Design System, false otherwise.
 */
export function isBuiltinDS(type, field) {
  if (!type || !field) {
    return false
  }

  const defaultData = this.getDefaults('DS_DEFAULTS') // await this.getDefaultDesignSystem()

  return Boolean(defaultData?.[type.toUpperCase()]?.[field])
}

/**
 * Updates a specific field in the Design System.
 *
 * Three call shapes are supported (legacy modals + new code use all three):
 *   updateDesignSystem('icons.iconName', svgText)        // string path
 *   updateDesignSystem(['icons', iconName], svgText)     // array path
 *   updateDesignSystem({ icons: { [name]: svgText } })   // object — Bug #2050:
 *     previously this threw on `key.split('.')` so the upload modal saved
 *     NOTHING to MongoDB. The preview iframe then fetched a snapshot with
 *     no uploaded icons, so `<Icon name="myIcon">` rendered nothing.
 *
 * @param {string | string[] | object} key - Path key, array, or object literal.
 * @param {*} [val] - Value when using string/array path form.
 * @param {object} [opts={}] - Options.
 */
export async function updateDesignSystem(key, val, opts = {}) {
  // Object form: build one tuple per top-level category and emit them in a
  // single sdk.updateData batch. Categories live at lowercase keys in the
  // stored data (matches example_tmr.json + scratch CONFIG + Icon.js's
  // `ctx.designSystem.icons` lookup) — do NOT uppercase paths anymore;
  // doing so silently writes to a parallel `ICONS` / `COLOR` namespace
  // that no consumer reads from, which was the underlying #2050 issue.
  if (key && typeof key === 'object' && !Array.isArray(key)) {
    const objectOpts = val && typeof val === 'object' && !Array.isArray(val)
      ? { ...val, ...opts }
      : opts
    const tuples = []
    const touchedTypes = []
    for (const category in key) {
      if (!Object.prototype.hasOwnProperty.call(key, category)) continue
      const entries = key[category]
      if (entries && typeof entries === 'object' && !Array.isArray(entries)) {
        // { icons: { iconName: '<svg>' } } → one tuple per leaf so we
        // don't clobber sibling icons stored under the same category.
        for (const leaf in entries) {
          if (!Object.prototype.hasOwnProperty.call(entries, leaf)) continue
          const value = entries[leaf]
          const path = ['designSystem', category, leaf]
          tuples.push(value != null ? ['update', path, value] : ['delete', path])
        }
        touchedTypes.push(category)
      } else {
        // Scalar / null replaces the whole category — used by theme flip etc.
        const path = ['designSystem', category]
        tuples.push(entries != null ? ['update', path, entries] : ['delete', path])
        touchedTypes.push(category)
      }
    }
    if (tuples.length) {
      this.sdk.updateData(tuples, {
        message: objectOpts.message || `Updated ${touchedTypes.join(', ')}`,
        ...objectOpts
      })
    }
    if (touchedTypes.includes('MEDIA') || touchedTypes.includes('media')) {
      this.getCanvas()?.update()
    } else {
      this.updateDesignSystemOnCanvas(this.getDesignSystem(), objectOpts)
    }
    if (!objectOpts.preventSidebarUpdate) {
      for (const t of touchedTypes) {
        let sectionType = t.toLowerCase()
        if (objectOpts.isSemanticColor) sectionType = 'semantic_color'
        else if (objectOpts.isGlobalTheme) sectionType = 'global_theme'
        else if (objectOpts.isTextStyles) sectionType = 'templates'
        const updatedSection = await this.call(
          'rerenderFileSidebarByType',
          sectionType
        )
        if (!updatedSection) await this.getDynamicIsland()?.update()
      }
    }
    return
  }

  const path = ['designSystem'].concat(
    Array.isArray(key) ? key : key.split('.')
  )

  this.sdk.updateData(val ? [['update', path, val]] : [['delete', path]], {
    message: val ? `Updated ${key}` : `Removed ${key}`,
    ...opts
  })

  const type = path[path.length - 2]
  if (type === 'MEDIA' || type === 'media') {
    this.getCanvas().update()
  } else {
    this.updateDesignSystemOnCanvas(this.getDesignSystem(), opts)
  }
  if (!opts.preventSidebarUpdate) {
    let sectionType = String(type || '').toLowerCase()
    if (opts.isSemanticColor) {
      sectionType = 'semantic_color'
    } else if (opts.isGlobalTheme) {
      sectionType = 'global_theme'
    } else if (opts.isTextStyles) {
      sectionType = 'templates'
    }

    const updatedSection = await this.call(
      'rerenderFileSidebarByType',
      sectionType
    )
    if (!updatedSection) {
      await this.getDynamicIsland()?.update()
    }
  }
}
/**
 * Updates a specific field in the Design System.
 * @param {*} changes Object Array { type: "update" | "delete", path: string[], value: any }
 */
export function bulkUpdateDesignSystem(changes, message, opts = {}) {
  if (!message || !changes) {
    return
  }

  const parsed = changes.map(({ type, path: p, value }) => {
    const path = p.includes('designSystem') ? p : ['designSystem', ...p]

    return value ? [type, path, value] : [type, path]
  })
  this.sdk.updateData(parsed, {
    message,
    ...opts
  })

  if (!opts.preventSidebarUpdate) {
    this.lookup('DynamicIsland')?.update()
  }
  this.updateDesignSystemOnCanvas(this.getDesignSystem(), opts)
}

export function activeAddingFloatingPanel(event, type) {
  const Section = this.call('findFileSidebarSection', type)

  if (!Section) {
    return
  }

  Section?.node?.scrollIntoView({
    behavior: 'smooth',
    inline: 'center',
    block: 'center'
  })

  const t = setTimeout(() => {
    const AddButton = Section.lookdown('IconButton.add')
    AddButton.onClick(event, AddButton, AddButton.state)
    clearTimeout(t)
  }, 350)
}
